/**
 * Budget-reset anomaly dashboard — Zod schemas (the single source of wire truth).
 *
 * Mirrors the Item 3 read surface (budget-reset gaming remediation):
 *   GET /api/v1/tenants/{tenant_id}/agents/budget-reset-anomalies
 * The detector is the ONLY writer; this surface is observe-only. Every anomaly is one append-only
 * row of the tamper-evident ledger — a detected re-provision "reset" of a cumulative budget.
 *
 * Semantics that drive the UI (from the Go detector):
 *   - carried_forward=true  → the ONLY caught case (reprovision_resume): the running total DID
 *                             survive across the lineage edge. Benign — muted in the fleet view.
 *   - carried_forward=false → the reset went SILENT (sever_via_delete / fresh_identity_suspected /
 *                             standing_delegation_reset). The control gap the operator must see.
 */

import { z } from "zod";

// The four behavioral signatures of a budget reset (mirrors budgetresetanomaly.SignatureKind).
export const anomalySignatureSchema = z.enum([
  "reprovision_resume",
  "sever_via_delete",
  "fresh_identity_suspected",
  "standing_delegation_reset",
]);
export type AnomalySignature = z.infer<typeof anomalySignatureSchema>;

// Detection confidence (mirrors budgetresetanomaly.Confidence).
export const anomalyConfidenceSchema = z.enum(["high", "medium", "low"]);
export type AnomalyConfidence = z.infer<typeof anomalyConfidenceSchema>;

// One detected anomaly (mirrors budgetresetanomalyadmin.anomalyDTO). Amounts are smallest-unit
// integers (humanized client-side where a dimension map is available; the fleet table shows the
// raw dimension key + pct-of-cap, which is unit-agnostic). detected_at is an http.TimeFormat string.
export const anomalySchema = z.object({
  id: z.string(),
  successor_agent_id: z.string(),
  predecessor_agent_id: z.string().optional(),
  dimension_key: z.string(),
  window_kind: z.string(),
  window_seconds: z.number().int().optional(),
  cap_amount: z.number(),
  predecessor_used: z.number(),
  pct_of_cap: z.number(),
  carried_forward: z.boolean(),
  signature_kind: z.string(),
  confidence: z.string(),
  detected_at: z.string(),
  // Sweep job + correlation basis (suspected-predecessor id, orphaned ledger id, pct threshold…).
  // Kept a permissive record — provenance is diagnostic, not something the UI hard-types.
  provenance: z.record(z.string(), z.unknown()).optional(),
});
export type Anomaly = z.infer<typeof anomalySchema>;

export const anomalyListResponseSchema = z.object({
  anomalies: z.array(anomalySchema).default([]),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type AnomalyListResponse = z.infer<typeof anomalyListResponseSchema>;

// ── URL-as-source-of-truth list/filter state ────────────────────────────────
// Every value degrades to undefined on an unknown/hand-edited value (never hard-errors the route).
export const anomaliesSearchSchema = z.object({
  signature_kind: anomalySignatureSchema.optional().catch(undefined),
  confidence: anomalyConfidenceSchema.optional().catch(undefined),
  // Tri-state: undefined = any; false = the actionable un-caught resets (the default focus); true =
  // the benign caught rows. Coerced from the ?carried=... string so the URL stays shareable.
  carried_forward: z.boolean().optional().catch(undefined),
  // Half-open [from, to) detected_at window (YYYY-MM-DD or RFC3339). Scopes BOTH the dashboard
  // list and the CSV export to an audit period, so the export inherits exactly what is shown.
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
  offset: z.coerce.number().int().nonnegative().optional().catch(undefined),
});
export type AnomaliesSearch = z.infer<typeof anomaliesSearchSchema>;
